import * as wellknown from 'wellknown';

/**
 * Interface for GeoJSON Feature following the GeoJSON specification
 */
export interface GeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: number[][][] | number[][];
  };
  properties: Record<string, any>;
}

/**
 * Interface for polygon coordinates
 */
export interface PolygonCoordinates {
  lat: number;
  lng: number;
}

/**
 * Interface for map bounds
 */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Converts WKT (Well-Known Text) to GeoJSON format
 * @param wkt - WKT string to convert
 * @returns GeoJSON feature or null if invalid
 */
export const wktToGeoJSON = (wkt: string): GeoJSONFeature | null => {
  if (!wkt || !wkt.trim()) {
    return null;
  }

  try {
    const geoJSON = wellknown.parse(wkt.trim());
    if (geoJSON && geoJSON.type === 'Polygon') {
      // Validate coordinates before returning
      if (!validateGeoJSONCoordinates(geoJSON.coordinates)) {
        return null;
      }
      
      return {
        type: 'Feature',
        geometry: geoJSON,
        properties: {}
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Validates GeoJSON coordinates structure and values
 * @param coordinates - GeoJSON coordinates array
 * @returns boolean indicating if coordinates are valid
 */
const validateGeoJSONCoordinates = (coordinates: number[][][] | number[][]): boolean => {
  if (!coordinates || !Array.isArray(coordinates)) {
    return false;
  }

  // For polygon coordinates, expect array of arrays of arrays
  if (coordinates.length === 0 || !Array.isArray(coordinates[0])) {
    return false;
  }

  // Check each coordinate pair
  for (const ring of coordinates) {
    if (!Array.isArray(ring) || ring.length < 3) {
      return false; // Polygon needs at least 3 points
    }

    for (const point of ring) {
      if (!Array.isArray(point) || point.length < 2) {
        return false;
      }

      const [lng, lat] = point;
      if (typeof lng !== 'number' || typeof lat !== 'number' || 
          isNaN(lng) || isNaN(lat) ||
          lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Validates WKT format
 * @param wkt - WKT string to validate
 * @returns boolean indicating if WKT is valid
 */
export const validateWKT = (wkt: string): boolean => {
  if (!wkt || !wkt.trim()) {
    return false;
  }

  try {
    const result = wellknown.parse(wkt.trim());
    return result !== null && result.type === 'Polygon';
  } catch {
    return false;
  }
};

/**
 * Converts GeoJSON coordinates to Leaflet format
 * @param coordinates - GeoJSON coordinates array
 * @returns Array of [lat, lng] pairs for Leaflet
 */
export const convertCoordinatesToLeaflet = (coordinates: number[][][]): PolygonCoordinates[] => {
  if (!coordinates || !coordinates[0]) {
    return [];
  }

  return coordinates[0]
    .filter(coord => coord && coord.length >= 2) // Filter out invalid coordinates
    .map(coord => {
      const [lng, lat] = coord;
      
      // Validate coordinate values before returning
      if (typeof lng === 'number' && typeof lat === 'number' && 
          !isNaN(lng) && !isNaN(lat) &&
          lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          lat: lat,
          lng: lng
        };
      }
      
      // Return null for invalid coordinates, filter out later
      return null;
    })
    .filter((coord): coord is PolygonCoordinates => coord !== null); // Type guard to remove nulls
};

/**
 * Calculates bounds for a polygon
 * @param coordinates - Polygon coordinates
 * @returns Map bounds object
 */
export const calculatePolygonBounds = (coordinates: number[][][]): MapBounds | null => {
  if (!coordinates || !coordinates[0] || coordinates[0].length === 0) {
    return null;
  }

  const points = coordinates[0];
  
  // Safely access first point with validation
  const firstPoint = points[0];
  if (!firstPoint || firstPoint.length < 2) {
    return null;
  }

  let north = firstPoint[1];
  let south = firstPoint[1];
  let east = firstPoint[0];
  let west = firstPoint[0];

  // Validate each point before processing
  for (const point of points) {
    if (!point || point.length < 2) {
      continue; // Skip invalid points
    }

    const [lng, lat] = point;
    
    // Validate coordinate values
    if (typeof lng !== 'number' || typeof lat !== 'number' || 
        isNaN(lng) || isNaN(lat)) {
      continue; // Skip invalid coordinates
    }

    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lng > east) east = lng;
    if (lng < west) west = lng;
  }

  return { north, south, east, west };
};

/**
 * Gets the center point of a polygon
 * @param coordinates - Polygon coordinates
 * @returns Center point [lat, lng] or null if invalid
 */
export const getPolygonCenter = (coordinates: number[][][]): [number, number] | null => {
  const bounds = calculatePolygonBounds(coordinates);
  if (!bounds) {
    return null;
  }

  const lat = (bounds.north + bounds.south) / 2;
  const lng = (bounds.east + bounds.west) / 2;
  
  return [lat, lng];
};

/**
 * Default map center (Delhi, India) - following the pattern from the existing code
 */
export const DEFAULT_MAP_CENTER: [number, number] = [28.6139, 77.2090];

/**
 * Default map zoom level
 */
export const DEFAULT_MAP_ZOOM = 13;

/**
 * Map tile layer configuration
 */
export const MAP_TILE_LAYER = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
};

/**
 * Polygon style configuration
 */
export const POLYGON_STYLE = {
  color: '#1890ff',
  weight: 2,
  fillColor: '#91d5ff',
  fillOpacity: 0.3,
  dashArray: '5, 5'
};

/**
 * Calculates the best map center when both polygon and marker are present
 * @param polygonCoordinates - Polygon coordinates
 * @param markerPosition - Marker position [lat, lng]
 * @returns Best center point [lat, lng]
 */
export const calculateOptimalCenter = (
  polygonCoordinates?: number[][][],
  markerPosition?: [number, number]
): [number, number] => {
  // If only marker, use marker position
  if (markerPosition && !polygonCoordinates) {
    return markerPosition;
  }

  // If only polygon, use polygon center
  if (polygonCoordinates && !markerPosition) {
    return getPolygonCenter(polygonCoordinates) || DEFAULT_MAP_CENTER;
  }

  // If both present, calculate midpoint between polygon center and marker
  if (polygonCoordinates && markerPosition) {
    const polygonCenter = getPolygonCenter(polygonCoordinates);
    if (polygonCenter) {
      const lat = (polygonCenter[0] + markerPosition[0]) / 2;
      const lng = (polygonCenter[1] + markerPosition[1]) / 2;
      return [lat, lng];
    }
    return markerPosition;
  }

  // Fallback to default
  return DEFAULT_MAP_CENTER;
};

/**
 * Safely parses a coordinate value from string or number
 * @param value - Value to parse
 * @returns Parsed number or null if invalid
 */
export const parseCoordinateValue = (value: string | number): number | null => {
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      return null;
    }
    
    const parsed = parseFloat(trimmed);
    return isNaN(parsed) ? null : parsed;
  }
  
  return null;
};

/**
 * Validates latitude and longitude values
 * @param lat - Latitude value (string or number)
 * @param lng - Longitude value (string or number)
 * @returns boolean indicating if coordinates are valid
 */
export const validateCoordinates = (lat: string | number, lng: string | number): boolean => {
  const latNum = parseCoordinateValue(lat);
  const lngNum = parseCoordinateValue(lng);

  return latNum !== null && lngNum !== null && 
         latNum >= -90 && latNum <= 90 && 
         lngNum >= -180 && lngNum <= 180;
};

/**
 * Safely parses and validates coordinate pair
 * @param lat - Latitude value (string or number)
 * @param lng - Longitude value (string or number)
 * @returns Parsed coordinate pair or null if invalid
 */
export const parseCoordinatePair = (lat: string | number, lng: string | number): [number, number] | null => {
  const latNum = parseCoordinateValue(lat);
  const lngNum = parseCoordinateValue(lng);

  if (latNum === null || lngNum === null ||
      latNum < -90 || latNum > 90 ||
      lngNum < -180 || lngNum > 180) {
    return null;
  }

  return [latNum, lngNum];
};

/**
 * Validates that coordinates fall within India's geographic boundaries
 * Latitude: 8.0 to 37.1
 * Longitude: 68.0 to 97.5
 */
export const validateIndianCoordinates = (lat: string | number, lng: string | number): boolean => {
  const latNum = parseCoordinateValue(lat);
  const lngNum = parseCoordinateValue(lng);

  if (latNum === null || lngNum === null) return false;

  return (
    latNum >= 8.0 && latNum <= 37.1 &&
    lngNum >= 68.0 && lngNum <= 97.5
  );
}; 